import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { play } from "../booth/webrtc.js";
import { TEAM } from "../booth/teams.js";
import { liveHref } from "../gp85/season.js";
import { seriesView, standingWords, useSeries } from "../booth/series.js";
import { DEMO_GAME, DEMO_POSTER, DEMO_STATE, isDemo } from "../booth/homeDemo.js";
import {
  adoptSession,
  flowWatch,
  goLive,
  holdSession,
  noteReconnect,
  recall,
  remember,
  takeSession,
} from "../booth/viewer.js";

// The home page while Greg is on the air: the broadcast takes the whole hero.
// His live picture, as big as the screen has room for, in the middle; above
// it the ON AIR sign with the clock, "Greg Pryor is live." and how many are in
// the booth; on the picture a lower-third strap with what he's calling and
// the score; under it the one bar to press ("Watch live with Greg"), a line
// on where tonight's booth is watching from, and the last few things said in
// The Stands, so the room reads as full before anyone clicks in. Behind it
// all, the picture itself, blurred into the room's light. Scroll past and he
// keeps playing in a small dock at the corner of the screen.
//
// Off the air none of this is here: hero.jsx draws its own copy and strip.
// /?booth=demo draws it with a still in the picture's place (homeDemo.js).
//
// Clicking in opens /live without a page load and hands it this connection
// (src/booth/viewer.js), so the picture never stops; coming Back hands it
// back. The picture keeps streaming through short trips away (another tab,
// a scroll down the page) and hangs up after longer ones, holding the last
// frame on screen while it reconnects.

const STATE_MS = 20_000; // off the air
const LIVE_MS = 10_000; // on the air: the talk and the count ride on it
const HIDDEN_GRACE_MS = 60_000; // tab in the background
const OFFSCREEN_GRACE_MS = 20_000; // hero scrolled away with the dock closed
const TALK_LINES = 3;

function usePolled(url, ms, kind) {
  const [data, setData] = useState(() => (url ? recall(kind) : null));
  useEffect(() => {
    if (!url) {
      setData(null);
      return undefined;
    }
    let stop = false;
    let timer;
    const tick = async () => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        const j = await r.json();
        if (!stop) {
          setData(r.ok ? j : null);
          if (r.ok) remember(kind, { ...j, _at: Date.now() });
        }
      } catch {
        /* no booth API (local dev): stays off */
      }
      if (!stop) timer = setTimeout(tick, document.hidden ? ms * 3 : ms);
    };
    tick();
    // Back from another tab or the Back button: catch up now.
    const back = () => {
      if (document.visibilityState !== "visible" || stop) return;
      clearTimeout(timer);
      tick();
    };
    document.addEventListener("visibilitychange", back);
    window.addEventListener("pageshow", back);
    return () => {
      stop = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", back);
      window.removeEventListener("pageshow", back);
    };
  }, [url, ms, kind]);
  return data;
}

// True while `on`, and for `grace` ms after it goes false.
function useGrace(on, grace) {
  const [kept, setKept] = useState(on);
  useEffect(() => {
    if (on) {
      setKept(true);
      return undefined;
    }
    const id = setTimeout(() => setKept(false), grace);
    return () => clearTimeout(id);
  }, [on, grace]);
  return on || kept;
}

function useOnScreen(ref) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return on;
}

function useVisible() {
  const [visible, setVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState === "visible",
  );
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

// Paints the live picture (or the demo still) onto a canvas every `ms`: the
// room's light behind the hero at one frame a second, the dock's small
// picture at eight. Drawing from the <video> leaves the stream alone.
function useMirror(canvasRef, mediaRef, ms, on) {
  useEffect(() => {
    if (!on) return undefined;
    const draw = () => {
      const c = canvasRef.current;
      const m = mediaRef.current;
      if (!c || !m) return;
      const ready =
        m.tagName === "VIDEO"
          ? m.readyState >= 2 && m.videoWidth > 0
          : m.complete && m.naturalWidth;
      if (!ready) return;
      try {
        c.getContext("2d").drawImage(m, 0, 0, c.width, c.height);
      } catch {}
    };
    draw();
    const id = setInterval(draw, ms);
    return () => clearInterval(id);
  }, [canvasRef, mediaRef, ms, on]);
}

// Greg's picture, muted. Takes a connection handed over from /live if there
// is one; retries until frames flow; reconnects if the connection goes dead or
// the page comes back from the browser's back/forward cache. With a `poster`
// (the demo) it's that still instead, and nothing connects.
function LiveVideo({ whep, active, poster, mediaRef }) {
  const ref = useRef(null);
  const still = useRef(null);
  const [playing, setPlaying] = useState(!!poster);
  const [nonce, setNonce] = useState(0);
  const seenNonce = useRef(0);
  const leaving = useRef(false);
  useEffect(
    () => () => {
      leaving.current = true;
    },
    [],
  );
  useEffect(() => {
    const onShow = (e) => {
      if (e.persisted) setNonce((n) => n + 1);
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  useEffect(() => {
    if (poster) {
      setPlaying(true);
      return undefined;
    }
    // A new nonce (back from the back/forward cache) forces a fresh
    // connection, and that counts as connecting again.
    const back = nonce !== seenNonce.current;
    seenNonce.current = nonce;
    if (!whep || !active) {
      setPlaying(false);
      return undefined;
    }
    let cancelled = false;
    let session = null;
    let retry = 0;
    // Only the newest start() keeps its connection; an older one still
    // connecting hangs up when it's through (see BoothWindow in live.jsx).
    let attempt = 0;
    // Why the next start() is connecting again (noteReconnect, for /live's
    // presence ping): the first thing that noticed, counted once.
    let cause = back ? "back" : "";
    const again = (ms, why) => {
      if (!cause) cause = why;
      clearTimeout(retry);
      retry = setTimeout(start, ms);
    };
    async function start() {
      const v = ref.current;
      if (cancelled || !v) return;
      const mine = ++attempt;
      const current = () => !cancelled && attempt === mine;
      if (cause) noteReconnect(cause);
      cause = "";
      session?.stop();
      session = null;
      const onState = (st) => {
        if (!current()) return;
        if (st === "connected") setPlaying(true);
        else if (st === "failed" || st === "closed") {
          setPlaying(false);
          again(3000, "failed");
        }
      };
      try {
        const handed = takeSession(whep);
        const s = handed ? adoptSession(handed, v, onState) : await play(whep, v, { onState });
        if (!current()) {
          s.stop();
          return;
        }
        session = s;
        v.play().catch(() => {});
      } catch {
        if (current()) again(5000, "error");
      }
    }
    start();
    // A dead connection (nothing arriving) gets a new one; see flowWatch.
    const flow = flowWatch();
    const watch = setInterval(async () => {
      const v = ref.current;
      if (!v || !session || document.visibilityState !== "visible") {
        flow.reset();
        return;
      }
      if (v.paused) v.play().catch(() => {});
      const why = await flow.check(session);
      if (why && !cancelled) {
        setPlaying(false);
        again(0, why);
      }
    }, 1000);
    return () => {
      cancelled = true;
      clearTimeout(retry);
      clearInterval(watch);
      // Keep the last frame up while it's gone (and while it reconnects).
      const v = ref.current;
      try {
        if (v && still.current && v.videoWidth) {
          still.current.width = v.videoWidth;
          still.current.height = v.videoHeight;
          still.current.getContext("2d").drawImage(v, 0, 0);
        }
      } catch {}
      // Leaving the page (clicking in to /live): hand the picture over.
      if (leaving.current) holdSession(whep, session);
      else session?.stop();
    };
  }, [whep, active, nonce, poster]);
  const setMedia = (el) => {
    ref.current = el;
    if (mediaRef) mediaRef.current = el;
  };
  return (
    <>
      <canvas ref={still} className="hl-still" />
      {poster ? (
        <img ref={setMedia} className="hl-video is-on" src={poster} alt="" decoding="async" />
      ) : (
        <video
          ref={setMedia}
          className={`hl-video${playing ? " is-on" : ""}`}
          muted
          playsInline
          autoPlay
        />
      )}
      {!playing && (
        <span className="hl-wait" aria-hidden="true">
          <i />
        </span>
      )}
    </>
  );
}

const shortSeries = (s = "") =>
  /^AL Div/.test(s)
    ? "ALDS"
    : /^NL Div/.test(s)
      ? "NLDS"
      : /^AL Champ/.test(s)
        ? "ALCS"
        : /^NL Champ/.test(s)
          ? "NLCS"
          : /^World/.test(s)
            ? "World Series"
            : /Wild Card/.test(s)
              ? "Wild Card"
              : s;

function Chip({ abbr }) {
  const [t, t2] = TEAM[abbr] || ["#1a3658", "#f4f1ea"];
  return (
    <span className="hl-chip" style={{ "--t": t, "--t2": t2 }}>
      {abbr}
    </span>
  );
}

function Play() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

function Arrow() {
  return (
    <svg className="hl-cta-arrow" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 12h13M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// How long he's been on, ticking: "1:04:09".
function useOnAirClock(startedAt) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return "";
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const two = (n) => String(n).padStart(2, "0");
  return `${Math.floor(s / 3600)}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}`;
}

// "Olathe, Omaha and St. Louis"
const listWords = (xs) =>
  xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;

// The same name colours as The Stands on /live.
const PAL = ["#2b8aff", "#d4b67a", "#4fbf8a", "#ff8a80", "#7fb4ff", "#b39ddb"];
const colorOf = (s) => {
  let h = 0;
  for (const ch of String(s || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PAL[h % PAL.length];
};

// The booth's public state, polled: 20 s off the air, 10 s on. Pages that
// already poll it pass `booth` in, and this does nothing of its own then.
export function useBoothState(given) {
  const [pace, setPace] = useState(STATE_MS);
  const polled = usePolled(given === undefined ? "/api/booth/state" : null, pace, "state");
  const booth = given === undefined ? polled : given;
  const live = !!booth?.live;
  useEffect(() => {
    setPace(live ? LIVE_MS : STATE_MS);
  }, [live]);
  return booth;
}

export default function HeroLive({ onLive, booth: given }) {
  const demo = isDemo();
  const polled = useBoothState(demo ? null : given);
  const booth = demo ? DEMO_STATE : polled;
  const live = !!booth?.live;
  const polledGame = usePolled(
    !demo && live && booth.mode !== "open" && booth.gamePk
      ? `/api/booth/game?pk=${booth.gamePk}`
      : null,
    LIVE_MS,
    "game",
  );
  const game = demo ? DEMO_GAME : polledGame;
  // Warm up /live's code while he's on, so clicking in is instant.
  useEffect(() => {
    if (live && !demo) import("./live.jsx").catch(() => {});
  }, [live, demo]);
  useEffect(() => {
    onLive?.(live);
  }, [live, onLive]);
  return live ? <LiveTakeover booth={booth} game={game} demo={demo} /> : null;
}

// Mounted only while Greg's live, so its on-screen watcher has the stage to watch.
function LiveTakeover({ booth, game, demo }) {
  // Where Watch goes: the night's page while one of the winter's tapes is on.
  const watch = liveHref(booth);
  const stageRef = useRef(null);
  const mediaRef = useRef(null);
  const ambientRef = useRef(null);
  const onScreen = useOnScreen(stageRef);
  const visible = useVisible();
  // Scrolled past the hero, he keeps playing in the dock until it's closed.
  const [dockClosed, setDockClosed] = useState(false);
  const docked = !onScreen && !dockClosed;
  // Short trips away keep the picture; long ones hang up until you're back.
  const nearby = useGrace(onScreen || docked, OFFSCREEN_GRACE_MS);
  const inFront = useGrace(visible, HIDDEN_GRACE_MS);
  const active = nearby && inFront;
  const clock = useOnAirClock(booth.startedAt);
  useMirror(ambientRef, mediaRef, 1000, active && onScreen);

  const series = seriesView(useSeries());
  const g = game?.ok ? game : null;
  const T = g?.teams;
  const S = g?.sit;
  const L = g?.line;
  const open = booth.mode === "open";
  const inGame = g?.game?.state === "Live";
  const half = S ? { Top: "▲", Bottom: "▼", Middle: "Mid", End: "End" }[S.half] || "" : "";
  const seriesName = g?.game?.series ? shortSeries(g.game.series) : "";
  const gameLabel = seriesName
    ? `${seriesName}${g.game.gameNo ? ` Game ${g.game.gameNo}` : ""}`
    : "";
  const standing =
    series.phase !== "none" && series.phase !== "over"
      ? standingWords(series)
      : T && g?.game?.seriesStatus
        ? g.game.seriesStatus
            .replace(T.away.abbr, T.away.club)
            .replace(T.home.abbr, T.home.club)
            .replace(/\bleads\b/, "lead")
        : "";
  const channel = g?.game?.tv ? `on ${String(g.game.tv).split("/")[0].trim()}` : "";
  const title = open
    ? booth.title || "Stories, questions and the '85 Royals"
    : T
      ? `${T.away.club} at ${T.home.club}`
      : "Greg's calling tonight's game";
  const sub = open
    ? "No game tonight: just Greg"
    : [gameLabel, channel, standing].filter(Boolean).join(" · ");
  const kicker = booth.brb
    ? "Greg's on a quick break"
    : booth.away
      ? "Greg stepped away for a minute"
      : "Greg's live in the booth";
  const viewers = booth.viewers > 0 ? booth.viewers : 0;

  // "He's calling Game 5 of the World Series pitch by pitch from his living
  // room. Tonight's booth is watching from Olathe, Omaha and 12 other towns."
  const towns = booth.towns || [];
  const named = towns.slice(0, 3).map((t) => t.t);
  const more = towns.length - named.length;
  const townWords = named.length
    ? `${listWords(named)}${more > 0 ? ` and ${more} other ${more === 1 ? "town" : "towns"}` : ""}`
    : "";
  const what = open
    ? "No game tonight: stories, your questions and the '85 Royals, from his living room."
    : `He's calling ${
        gameLabel && g.game.gameNo
          ? `Game ${g.game.gameNo} of the ${seriesName}`
          : T
            ? `${T.away.club} at ${T.home.club}`
            : "tonight's game"
      } pitch by pitch from his living room.`;
  const sentence = townWords ? `${what} Tonight's booth is watching from ${townWords}.` : what;

  const talk = (booth.stands?.msgs || []).slice(-TALK_LINES);
  const onId = booth.stands?.on?.id || null;

  const label = `Greg is live now${T ? `, calling ${title}` : ""}. Watch live with Greg.`;

  return (
    <>
      <canvas ref={ambientRef} className="hl-ambient" width="48" height="27" />
      <div className={`hl-take${demo ? " is-demo" : ""}`}>
        <span className="hl-onair">
          <span className="hl-onair-sign">
            <i aria-hidden="true" />
            On air
          </span>
          {clock && <span className="hl-onair-clock">{clock}</span>}
        </span>
        <h1 className="hl-head">
          Greg Pryor <em>is live.</em>
        </h1>
        <span className="hl-seats">
          <i aria-hidden="true" />
          {viewers > 0 ? (
            <>
              <b>{viewers.toLocaleString()}</b> in the booth
            </>
          ) : (
            "The booth's just opening"
          )}
        </span>

        <a
          ref={stageRef}
          className="hl"
          href={watch}
          onClick={(e) => goLive(e, watch)}
          aria-label={label}
        >
          <span className="hl-frame">
            <LiveVideo
              whep={booth.whep}
              active={active}
              poster={demo ? DEMO_POSTER : null}
              mediaRef={mediaRef}
            />
            {demo && <span className="hl-demo">Preview · his live picture goes here</span>}
            <span className="hl-strap">
              <span className="hl-kicker">
                <i aria-hidden="true" />
                {kicker}
              </span>
              <b className="hl-title">{title}</b>
              {sub && <span className="hl-sub">{sub}</span>}
            </span>
            {T && L && (
              <span className="hl-bug">
                <span className="hl-bug-row">
                  <Chip abbr={T.away.abbr} />
                  <b>{L.away?.runs ?? 0}</b>
                </span>
                <span className="hl-bug-row">
                  <Chip abbr={T.home.abbr} />
                  <b>{L.home?.runs ?? 0}</b>
                </span>
                {inGame && S && (
                  <span className="hl-bug-inn">
                    {half} {S.inning}
                  </span>
                )}
              </span>
            )}
            <span className="hl-go" aria-hidden="true">
              <Play />
            </span>
          </span>
        </a>

        <div className="hl-bottom">
          <div className="hl-lead">
            <a className="hl-cta" href={watch} onClick={(e) => goLive(e, watch)}>
              <span className="hl-cta-play" aria-hidden="true">
                <Play />
              </span>
              <span className="hl-cta-text">
                <b>Watch live with Greg</b>
                <span>Free · No sign-up · Pull up a seat</span>
              </span>
              <Arrow />
            </a>
            <p className="hl-who">№ 4 · 1985 World Series champion · Ten MLB seasons</p>
            <p className="hl-sentence">{sentence}</p>
          </div>
          <a
            className="hl-talk"
            href={watch}
            onClick={(e) => goLive(e, watch)}
            aria-label="The Stands: what people are saying in the booth right now. Join in on the live page."
          >
            <span className="hl-talk-head">
              <span className="hl-talk-kicker">
                <i aria-hidden="true" />
                The Stands
              </span>
              <span className="hl-talk-go">Join the talk →</span>
            </span>
            <span className="hl-talk-lines">
              {talk.length ? (
                talk.map((m) => (
                  <span
                    key={m.id}
                    className={`hl-line${m.id === onId ? " is-on" : m.q ? " is-q" : ""}`}
                  >
                    <span className="hl-line-who">
                      <b style={{ "--c": colorOf(m.name) }}>{m.name}</b>
                      {m.from && <small>{m.from}</small>}
                    </span>
                    <span className="hl-line-text">
                      {m.text}
                      {m.id === onId ? <em>On Greg's screen</em> : m.q ? <em>Asked Greg</em> : null}
                    </span>
                  </span>
                ))
              ) : (
                <span className="hl-line is-empty">
                  <span className="hl-line-text">
                    The stands are quiet so far. Say the first thing.
                  </span>
                </span>
              )}
            </span>
          </a>
        </div>
      </div>
      {docked && (
        <Dock mediaRef={mediaRef} title={title} watch={watch} onClose={() => setDockClosed(true)} />
      )}
    </>
  );
}

// Scrolled past the hero: his picture, small, at the corner of the screen,
// still going. Lives on <body>: the hero's sections carry transforms, which
// would hold a fixed box inside them.
function Dock({ mediaRef, title, watch = "/live", onClose }) {
  const ref = useRef(null);
  useMirror(ref, mediaRef, 125, true);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="hl-dock" role="complementary" aria-label="Greg is live right now">
      <a className="hl-dock-link" href={watch} onClick={(e) => goLive(e, watch)}>
        <span className="hl-dock-pic">
          <canvas ref={ref} width="320" height="180" />
          <span className="hl-dock-tag">
            <i aria-hidden="true" />
            Live
          </span>
        </span>
        <span className="hl-dock-text">
          <b>Greg's live right now</b>
          <span>{title}</span>
        </span>
        <span className="hl-dock-play" aria-hidden="true">
          <Play />
        </span>
      </a>
      <button type="button" className="hl-dock-x" aria-label="Hide this" onClick={onClose}>
        ×
      </button>
    </div>,
    document.body,
  );
}

const css = `
/* The room's light: the picture itself, blurred, behind everything, from the
   very top of the page down, fading only into the section below. Blank until
   frames flow (the hero's own warm glows show through meanwhile). */
.hl-ambient {
  /* Runs past the top (the blur's soft edge lands above the page, not on
     it) and ends exactly at the hero's bottom, already faded out, so the
     section below meets it with no edge. No transform: a scaled canvas
     carries its fade past the hero and gets cut off there. */
  position: absolute; left: -6%; top: -18%; z-index: 2;
  width: 112%; height: 118%;
  opacity: 0.55;
  filter: blur(70px) saturate(1.6) brightness(0.85);
  -webkit-mask-image: linear-gradient(180deg, #000 0%, #000 64%, rgba(0, 0, 0, 0.35) 86%, transparent 97%);
  mask-image: linear-gradient(180deg, #000 0%, #000 64%, rgba(0, 0, 0, 0.35) 86%, transparent 97%);
  pointer-events: none;
}

/* Everything shares the picture's width, which is as wide as the screen's
   height allows (the sign row and the bar row take the rest). */
.hl-take {
  --hl-chrome: 400px;
  position: relative; z-index: 5;
  width: min(100% - 112px, 1208px, max(62vw, calc((100vh - var(--hl-chrome)) * 16 / 9)));
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas: "sign head seats" "pic pic pic" "bottom bottom bottom";
  align-items: center;
  column-gap: 18px; row-gap: 14px;
  color: var(--bone);
  animation: hl-in 800ms cubic-bezier(0.2, 0.9, 0.2, 1) both;
}
@keyframes hl-in { from { opacity: 0; translate: 0 22px; } }

/* The sign: ON AIR, and how long. */
.hl-onair { grid-area: sign; display: inline-flex; filter: drop-shadow(0 10px 24px rgba(255, 74, 61, 0.4)); }
.hl-onair-sign { display: inline-flex; align-items: center; gap: 9px; padding: 9px 14px 9px 12px; border-radius: 8px 0 0 8px; background: linear-gradient(180deg, #ff5a4c, #c8102e); color: #fff; font: 800 0.8125rem/1 var(--mono); letter-spacing: 0.26em; text-transform: uppercase; text-shadow: 0 0 12px rgba(255, 200, 190, 0.8); box-shadow: inset 0 0 0 1px rgba(255, 150, 140, 0.55), 0 0 26px rgba(255, 74, 61, 0.55); animation: hl-sign 2.4s ease-in-out infinite; white-space: nowrap; }
.hl-onair-sign i { width: 8px; height: 8px; border-radius: 50%; background: #fff; box-shadow: 0 0 10px #fff; animation: hl-blink 1.3s steps(2, jump-none) infinite; }
@keyframes hl-sign { 50% { box-shadow: inset 0 0 0 1px rgba(255, 150, 140, 0.55), 0 0 44px rgba(255, 74, 61, 0.85); } }
@keyframes hl-blink { 50% { opacity: 0.25; } }
.hl-onair-clock { display: inline-flex; align-items: center; padding: 9px 12px; border-radius: 0 8px 8px 0; background: rgba(5, 13, 26, 0.94); color: var(--bone); font: 600 0.8125rem/1 var(--mono); letter-spacing: 0.08em; font-variant-numeric: tabular-nums; box-shadow: inset 0 0 0 1px rgba(255, 74, 61, 0.55); }

/* The headline, beside the sign. */
.hl-head { grid-area: head; margin: 0; min-width: 0; font: 400 clamp(1.5rem, 2.3vw, 2.25rem)/1 var(--serif); letter-spacing: -0.025em; color: var(--bone); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hl-head em { font-style: italic; background: linear-gradient(135deg, #ffe0dc 0%, #ff6b5e 55%, var(--gold-bright) 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; padding-right: 0.08em; }

/* How many are in the booth. */
.hl-seats { grid-area: seats; display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; border-radius: 8px; background: rgba(5, 13, 26, 0.74); box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.35); font: 500 0.625rem/1 var(--mono); letter-spacing: 0.14em; text-transform: uppercase; color: var(--bone-dim); white-space: nowrap; }
.hl-seats i { width: 7px; height: 7px; border-radius: 50%; background: #4fbf8a; box-shadow: 0 0 10px rgba(79, 191, 138, 0.9); }
.hl-seats b { font: 600 1rem/1 var(--sans); letter-spacing: 0; color: var(--bone); }

/* The picture: as wide as the row, a thin ring red to gold turning around it. */
.hl { grid-area: pic; position: relative; display: block; width: 100%; color: var(--bone); text-decoration: none; }
.hl:focus-visible { outline: none; }
.hl:focus-visible .hl-frame { box-shadow: 0 0 0 3px var(--gold-bright), 0 0 60px -10px rgba(255, 74, 61, 0.45); }
.hl::before {
  content: ""; position: absolute; inset: -3px;
  border-radius: 21px; padding: 3px;
  background: conic-gradient(from var(--hl-a), rgba(255, 74, 61, 0.12), #ff4a3d 18%, #f2c27b 32%, rgba(255, 74, 61, 0.12) 52%, rgba(255, 74, 61, 0.12));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  animation: hl-ring 7s linear infinite;
  pointer-events: none;
}
@property --hl-a { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
@keyframes hl-ring { to { --hl-a: 360deg; } }
.hl-frame {
  position: relative; display: block; aspect-ratio: 16 / 9;
  border-radius: 18px; overflow: hidden;
  background: radial-gradient(60% 70% at 50% 40%, rgba(0, 70, 135, 0.4), transparent 70%), #02060c;
  box-shadow: 0 0 70px -10px rgba(255, 74, 61, 0.42), 0 60px 120px -40px rgba(0, 0, 0, 0.95);
  animation: hl-breathe 3.2s ease-in-out infinite;
  transition: box-shadow 220ms ease;
}
@keyframes hl-breathe { 50% { box-shadow: 0 0 100px -10px rgba(255, 74, 61, 0.58), 0 60px 120px -40px rgba(0, 0, 0, 0.95); } }
.hl-video, .hl-still { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: opacity 500ms ease, filter 300ms ease; }
.hl-video { opacity: 0; }
.hl-video.is-on { opacity: 1; }
.hl:hover .hl-video { filter: brightness(1.06); }
/* A touch of shade top and bottom, so the strap reads. */
.hl-frame::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(5, 13, 26, 0.28), transparent 22%, transparent 56%, rgba(5, 13, 26, 0.8)); pointer-events: none; }
.hl-wait { position: absolute; z-index: 2; inset: auto auto 14px 50%; translate: -50% 0; display: grid; place-items: center; }
.hl-wait i { width: 20px; height: 20px; border-radius: 50%; border: 2px solid rgba(232, 227, 214, 0.25); border-top-color: var(--gold-bright); animation: hl-spin 900ms linear infinite; }
@keyframes hl-spin { to { transform: rotate(360deg); } }
.hl-demo { position: absolute; z-index: 3; top: 14px; right: 14px; padding: 6px 9px; border-radius: 5px; background: rgba(5, 13, 26, 0.78); color: var(--gold-bright); font: 600 0.5625rem/1 var(--mono); letter-spacing: 0.14em; text-transform: uppercase; }

/* The lower third: what he's calling, like a name strap on /live. */
.hl-strap { position: absolute; z-index: 2; left: 18px; bottom: 18px; display: flex; flex-direction: column; align-items: flex-start; max-width: calc(100% - 150px); }
.hl-kicker { display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px 5px; background: rgba(5, 13, 26, 0.82); font: 600 0.625rem/1 var(--mono); letter-spacing: 0.16em; text-transform: uppercase; color: #ff8a80; }
.hl-kicker i { width: 7px; height: 7px; border-radius: 50%; background: #ff4a3d; box-shadow: 0 0 0 0 rgba(255, 74, 61, 0.6); animation: hl-pulse 1.6s ease-in-out infinite; }
@keyframes hl-pulse { 50% { box-shadow: 0 0 0 6px rgba(255, 74, 61, 0); } }
.hl-title { padding: 0.28em 0.7em 0.24em; background: rgba(5, 13, 26, 0.9); border-left: 3px solid var(--gold); font: 500 clamp(1.25rem, 2vw, 1.875rem)/1.12 var(--serif); letter-spacing: -0.01em; text-wrap: balance; }
.hl-sub { padding: 0.55em 0.9em; background: var(--royal-blue); font: 500 0.6875rem/1.25 var(--mono); letter-spacing: 0.12em; text-transform: uppercase; color: var(--bone); }

.hl-bug { position: absolute; z-index: 2; right: 16px; bottom: 18px; display: grid; grid-template-columns: auto auto; gap: 3px 10px; align-items: center; padding: 9px 11px; border-radius: 7px; background: rgba(5, 13, 26, 0.9); box-shadow: 0 0 0 1px rgba(189, 155, 96, 0.4); }
.hl-bug-row { display: contents; }
.hl-bug-row b { font: 600 1.25rem/1 var(--serif); text-align: right; font-variant-numeric: tabular-nums; }
.hl-bug-inn { grid-column: 1 / -1; text-align: center; font: 600 0.625rem/1 var(--mono); color: var(--gold-bright); padding-top: 3px; }
.hl-chip { display: inline-grid; place-items: center; min-width: 2.8em; padding: 0.28em 0.45em; border-radius: 3px; background: var(--t); color: var(--t2); font: 700 0.6875rem/1 var(--sans); letter-spacing: 0.05em; }

/* The picture's its own button: a play ring comes up when the pointer's on it. */
.hl-go { position: absolute; z-index: 3; left: 50%; top: 50%; translate: -50% -50%; display: grid; place-items: center; width: 92px; height: 92px; border-radius: 50%; background: rgba(5, 13, 26, 0.55); box-shadow: 0 0 0 2px rgba(244, 241, 234, 0.7), 0 0 0 12px rgba(255, 74, 61, 0.18); backdrop-filter: blur(6px); color: #fff; opacity: 0; scale: 0.85; transition: opacity 240ms ease, scale 320ms cubic-bezier(0.2, 0.9, 0.2, 1), background 240ms ease; pointer-events: none; }
.hl-go svg { width: 40px; height: 40px; margin-left: 4px; }
.hl:hover .hl-go, .hl:focus-visible .hl-go { opacity: 1; scale: 1; background: var(--crimson); }

/* Under the picture: the bar, a line on the booth, and the talk. */
.hl-bottom { grid-area: bottom; display: grid; grid-template-columns: minmax(0, 6fr) minmax(0, 4fr); gap: 16px 22px; align-items: start; }
.hl-lead { display: grid; gap: 12px; min-width: 0; }
.hl-cta {
  position: relative; overflow: hidden;
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 16px;
  width: 100%;
  padding: 12px 20px 12px 12px;
  border-radius: 16px;
  color: #fff; text-decoration: none;
  background: linear-gradient(135deg, #ff5d4f 0%, #c8102e 55%, #a30c27 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 140, 130, 0.45), 0 22px 44px -18px rgba(255, 74, 61, 0.8);
  transition: transform 220ms ease, box-shadow 220ms ease;
}
.hl-cta:hover { transform: translateY(-2px); box-shadow: inset 0 0 0 1px rgba(255, 170, 160, 0.6), 0 28px 54px -18px rgba(255, 74, 61, 0.95); }
.hl-cta:focus-visible { outline: 3px solid var(--gold-bright); outline-offset: 2px; }
/* A shine runs across it every few seconds. */
.hl-cta::after { content: ""; position: absolute; top: -40%; bottom: -40%; left: -30%; width: 22%; background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.32), transparent); transform: skewX(-18deg); animation: hl-shine 5s ease-in-out infinite; pointer-events: none; }
@keyframes hl-shine { 0%, 55% { left: -30%; } 100% { left: 130%; } }
.hl-cta-play { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 50%; background: #fff; color: #c8102e; box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.6); transition: transform 220ms ease; }
.hl-cta-play svg { width: 26px; height: 26px; margin-left: 3px; }
.hl-cta:hover .hl-cta-play { transform: scale(1.06); }
.hl-cta-text { display: grid; gap: 3px; min-width: 0; }
.hl-cta-text b { font: 700 clamp(1.125rem, 1.5vw, 1.375rem)/1.1 var(--sans); letter-spacing: -0.01em; }
.hl-cta-text span { font: 500 0.6875rem/1.3 var(--mono); letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255, 255, 255, 0.82); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hl-cta-arrow { width: 26px; height: 26px; color: #fff; transition: transform 220ms ease; }
.hl-cta:hover .hl-cta-arrow { transform: translateX(4px); }
.hl-who { margin: 2px 0 0; font: 600 0.625rem/1.4 var(--mono); letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold); }
.hl-sentence { margin: 0; font: 400 clamp(0.9375rem, 1.05vw, 1.0625rem)/1.5 var(--serif); color: var(--bone-dim); text-wrap: pretty; }

/* The Stands: the last few things said, newest at the bottom. */
.hl-talk { display: grid; align-content: start; gap: 10px; min-width: 0; padding: 14px 16px 13px; border-radius: 16px; background: rgba(5, 13, 26, 0.74); box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.3); color: var(--bone); text-decoration: none; transition: box-shadow 200ms ease, transform 200ms ease; }
.hl-talk:hover { box-shadow: inset 0 0 0 1px var(--gold); transform: translateY(-2px); }
.hl-talk:focus-visible { outline: 3px solid var(--gold-bright); outline-offset: 2px; }
.hl-talk-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.hl-talk-kicker { display: inline-flex; align-items: center; gap: 8px; font: 600 0.625rem/1 var(--mono); letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold); white-space: nowrap; }
.hl-talk-kicker i { width: 7px; height: 7px; border-radius: 50%; background: #4fbf8a; box-shadow: 0 0 0 0 rgba(79, 191, 138, 0.6); animation: hl-pulse-g 1.6s ease-in-out infinite; }
@keyframes hl-pulse-g { 50% { box-shadow: 0 0 0 6px rgba(79, 191, 138, 0); } }
.hl-talk-go { font: 600 0.625rem/1 var(--mono); letter-spacing: 0.12em; text-transform: uppercase; color: var(--royal-blue-glow); white-space: nowrap; transition: transform 200ms ease; }
.hl-talk:hover .hl-talk-go { transform: translateX(3px); }
.hl-talk-lines { display: grid; gap: 8px; }
.hl-line { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: baseline; gap: 8px; font: 400 0.875rem/1.35 var(--sans); animation: hl-line-in 500ms cubic-bezier(0.2, 0.9, 0.2, 1) both; }
@keyframes hl-line-in { from { opacity: 0; translate: 0 6px; } }
.hl-line-who { display: inline-flex; align-items: baseline; gap: 5px; white-space: nowrap; }
.hl-line-who b { font-weight: 700; color: var(--c, var(--bone)); }
.hl-line-who small { font: 500 0.625rem/1 var(--mono); letter-spacing: 0.06em; color: var(--bone-dim); opacity: 0.7; }
.hl-line-text { min-width: 0; overflow: hidden; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; color: var(--bone-dim); overflow-wrap: anywhere; }
.hl-line em { display: inline-block; margin-left: 7px; vertical-align: 1px; font: 600 0.5625rem/1 var(--mono); font-style: normal; letter-spacing: 0.12em; text-transform: uppercase; color: var(--gold-bright); white-space: nowrap; padding: 4px 6px; border-radius: 4px; background: rgba(189, 155, 96, 0.14); }
.hl-line.is-on em { background: rgba(255, 74, 61, 0.2); color: #ff8a80; }
.hl-line.is-on .hl-line-text { color: var(--bone); }
.hl-line.is-empty { grid-template-columns: minmax(0, 1fr); opacity: 0.75; }
.hl-line.is-empty .hl-line-text { -webkit-line-clamp: 3; }

/* The dock: scrolled past the hero, he's still on, small, at the corner. */
.hl-dock { position: fixed; right: 20px; bottom: 20px; z-index: 90; display: flex; align-items: stretch; width: min(420px, calc(100vw - 40px)); border-radius: 16px; background: rgba(5, 13, 26, 0.92); backdrop-filter: blur(12px); box-shadow: inset 0 0 0 1px rgba(255, 74, 61, 0.5), 0 24px 60px -20px rgba(0, 0, 0, 0.9), 0 0 44px -10px rgba(255, 74, 61, 0.4); overflow: hidden; animation: hl-dock-in 420ms cubic-bezier(0.2, 0.9, 0.2, 1) both; }
@keyframes hl-dock-in { from { opacity: 0; translate: 0 16px; } }
.hl-dock-link { flex: 1; display: grid; grid-template-columns: 124px minmax(0, 1fr) auto; align-items: center; gap: 12px; min-width: 0; padding: 8px 10px 8px 8px; color: var(--bone); text-decoration: none; }
.hl-dock-pic { position: relative; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden; background: #02060c; }
.hl-dock-pic canvas { display: block; width: 100%; height: 100%; }
.hl-dock-tag { position: absolute; left: 6px; top: 6px; display: inline-flex; align-items: center; gap: 5px; padding: 4px 6px; border-radius: 4px; background: #c8102e; color: #fff; font: 800 0.5625rem/1 var(--mono); letter-spacing: 0.16em; text-transform: uppercase; }
.hl-dock-tag i { width: 5px; height: 5px; border-radius: 50%; background: #fff; animation: hl-blink 1.3s steps(2, jump-none) infinite; }
.hl-dock-text { display: grid; gap: 4px; min-width: 0; }
.hl-dock-text b { font: 700 0.9375rem/1.15 var(--sans); letter-spacing: -0.01em; }
.hl-dock-text span { font: 500 0.625rem/1.3 var(--mono); letter-spacing: 0.1em; text-transform: uppercase; color: var(--bone-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hl-dock-play { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 50%; background: var(--crimson); color: #fff; transition: transform 200ms ease; }
.hl-dock-play svg { width: 18px; height: 18px; margin-left: 2px; }
.hl-dock-link:hover .hl-dock-play { transform: scale(1.08); }
.hl-dock-x { flex: none; width: 34px; border: 0; border-left: 1px solid rgba(255, 255, 255, 0.06); background: rgba(255, 255, 255, 0.04); color: var(--bone-dim); font: 400 1.25rem/1 var(--sans); cursor: pointer; }
.hl-dock-x:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }

@media (max-width: 1080px) {
  .hl-take { --hl-chrome: 420px; width: min(100% - 64px, max(68vw, calc((100vh - var(--hl-chrome)) * 16 / 9))); column-gap: 14px; row-gap: 12px; }
  .hl-head { font-size: clamp(1.375rem, 2.4vw, 1.75rem); }
  .hl-bottom { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px 16px; }
  .hl-title { font-size: clamp(1.125rem, 1.9vw, 1.5rem); }
}
/* Narrower screens: one column. The picture first, then the headline. */
@media (max-width: 860px) {
  .hl-take {
    width: min(100% - 48px, 600px);
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas: "sign seats" "pic pic" "head head" "bottom bottom";
    row-gap: 12px;
    animation-name: hl-in-flow;
  }
  @keyframes hl-in-flow { from { opacity: 0; translate: 0 12px; } }
  .hl-onair { justify-self: start; }
  .hl-onair-sign { padding: 7px 10px 7px 9px; font-size: 0.6875rem; letter-spacing: 0.2em; }
  .hl-onair-clock { padding: 7px 9px; font-size: 0.6875rem; }
  .hl-seats { padding: 7px 10px; font-size: 0.5625rem; }
  .hl-seats b { font-size: 0.875rem; }
  .hl-head { font-size: clamp(1.75rem, 8vw, 2.5rem); white-space: normal; margin-top: 2px; }
  .hl::before { inset: -3px; border-radius: 17px; }
  .hl-frame { border-radius: 14px; }
  .hl-demo { top: 10px; right: 10px; }
  .hl-strap { left: 10px; bottom: 10px; max-width: calc(100% - 110px); }
  .hl-kicker { font-size: 0.5625rem; letter-spacing: 0.12em; padding: 5px 8px 4px; }
  .hl-title { font-size: 1.0625rem; }
  .hl-sub { font-size: 0.5625rem; padding: 0.5em 0.8em; }
  .hl-bug { right: 10px; bottom: 10px; padding: 6px 8px; gap: 2px 8px; }
  .hl-bug-row b { font-size: 0.9375rem; }
  .hl-go { display: none; }
  .hl-bottom { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .hl-lead { gap: 10px; }
  .hl-cta { gap: 12px; padding: 10px 16px 10px 10px; border-radius: 14px; }
  .hl-cta-play { width: 42px; height: 42px; }
  .hl-cta-play svg { width: 20px; height: 20px; }
  .hl-cta-text b { font-size: 1.0625rem; }
  .hl-cta-text span { font-size: 0.625rem; }
  .hl-cta-arrow { width: 22px; height: 22px; }
  .hl-sentence { font-size: 0.9375rem; }
  .hl-talk { padding: 12px 14px 11px; border-radius: 14px; }
  .hl-line { font-size: 0.8125rem; }
  .hl-line:first-child { display: none; }
  .hl-dock { left: 12px; right: 12px; bottom: calc(12px + env(safe-area-inset-bottom)); width: auto; border-radius: 14px; }
  .hl-dock-link { grid-template-columns: 96px minmax(0, 1fr) auto; gap: 10px; padding: 6px 8px 6px 6px; }
  .hl-dock-text b { font-size: 0.875rem; }
}
@media (max-width: 480px) {
  .hl-take { width: calc(100% - 40px); }
  .hl-sub, .hl-bug-inn { display: none; }
  .hl-cta-text span { display: none; }
  .hl-cta { padding: 9px 14px 9px 9px; }
  .hl-cta-text b { font-size: 1rem; }
  .hl-line em { display: none; }
  .hl-dock-text span { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .hl-take, .hl-take *, .hl-dock, .hl-dock * { animation: none !important; transition: none !important; }
  .hl-cta::after { display: none; }
  .hl-go { opacity: 1; scale: 1; }
}
`;
if (typeof document !== "undefined") {
  const el = document.createElement("style");
  el.dataset.section = "hero-live";
  el.textContent = css;
  document.head.appendChild(el);
}
